import Select from '@components/v2/form/Select/Select';
import { SelectOptionValue } from '@components/v2/form/Select/select.types';
import TextField from '@components/v2/form/TextField/TextField';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, SelectChangeEvent } from '@mui/material';
import { FeatureFlagEnum } from '../../../../featureflag/feature-flag.enum';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import LangEnum from '../../../../lang/LangEnum';
import { onlyNumbers } from '../../../../utils/validators';
import { LangEnumOption } from '../../../business-policies/business-policy.options';
import { Role } from '../../../orders/actions/types/actions.types';
import { useTranslation } from '../../../translation/TranslationContext';
import SettingContainer from '../../SettingContainer';
import { StockSectionGeneralSettingsProps } from '../types/GeneralSettings.types';

export enum RolesToHideStockEnum {
  CUSTOMER = 'customer',
  REPRESENTATIVE = 'representative',
}

export const stockRolesOptions: LangEnumOption[] = [
  {
    value: RolesToHideStockEnum.REPRESENTATIVE,
    label: LangEnum.GLOBAL_SALES_REPRESENTATIVES,
  },
  {
    value: RolesToHideStockEnum.CUSTOMER,
    label: LangEnum.GLOBAL_CUSTOMER,
  },
];

const StockSectionGeneralSettings = ({
  generalSettings,
  handleChangeAndBlur,
  handleBlur,
  handleChange,
}: StockSectionGeneralSettingsProps) => {
  const { translate } = useTranslation();
  const { getFeatureFlag } = useFeatureFlag();
  const isLimitStockFieldEnabled = getFeatureFlag(FeatureFlagEnum.ENABLE_LIMIT_STOCK_FIELD);

  const mapToBackend = (values: string[]): Role[] => {
    const roles: Role[] = [];
    if (values.includes(RolesToHideStockEnum.CUSTOMER)) {
      roles.push(Role.COMPRADOR, Role.ADMIN_LOJA);
    }
    if (values.includes(RolesToHideStockEnum.REPRESENTATIVE)) {
      roles.push(Role.VENDEDOR);
    }
    return roles;
  };
  const mapFromBackend = (roles: Role[]) => {
    const values: RolesToHideStockEnum[] = [];
    if (roles.some((role) => role === Role.COMPRADOR || role === Role.ADMIN_LOJA)) {
      values.push(RolesToHideStockEnum.CUSTOMER);
    }
    if (roles.includes(Role.VENDEDOR)) {
      values.push(RolesToHideStockEnum.REPRESENTATIVE);
    }
    return values;
  };

  const selectedRoles = generalSettings.rolesToHideStock
    ? mapFromBackend(generalSettings.rolesToHideStock)
    : [];

  const changeRoles = (values: string[]) =>
    handleChangeAndBlur({
      name: 'rolesToHideStock',
      value: mapToBackend(values),
    });

  const handleChangeRoles = (event: SelectChangeEvent<unknown>) =>
    changeRoles(event.target.value as string[]);

  const handleDeleteRole = (deletedValue: SelectOptionValue) =>
    changeRoles(selectedRoles.filter((value) => value !== deletedValue));

  const disableMaximumValues =
    (generalSettings.rolesToHideStock && generalSettings.rolesToHideStock?.length === 3) ?? false;

  return (
    <SettingContainer
      title={translate(LangEnum.GLOBAL_STOCK)}
      description={LangEnum.GENERAL_SETTINGS_HIDE_STOCK_INTERNAL_MESSAGE}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography>{translate(LangEnum.GENERAL_SETTINGS_HIDE_STOCK_MESSAGE)}</Typography>
          <Select
            multiple
            label={translate(LangEnum.GENERAL_SETTINGS_ROLES_HIDE_STOCK_LABEL)}
            name="rolesToHideStock"
            value={selectedRoles}
            multipleProps={{
              onDelete: handleDeleteRole,
              getChipConfig: (value) => ({
                label: translate(stockRolesOptions.find((option) => option.value === value)!.label),
              }),
            }}
            onChange={handleChangeRoles}
          >
            {stockRolesOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {translate(option.label)}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {isLimitStockFieldEnabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography>{translate(LangEnum.GENERAL_SETTINGS_MAXIMUM_STOCK_MESSAGE)}</Typography>
            <Tooltip
              placement="bottom"
              TooltipContent={
                disableMaximumValues
                  ? translate(LangEnum.GENERAL_SETTINGS_MAXIMUM_STOCK_TOOLTIP)
                  : ''
              }
            >
              <TextField
                type="number"
                label={translate(LangEnum.GENERAL_SETTINGS_MAX_STOCK_TO_SHOW)}
                name="maxStockToShow"
                disabled={disableMaximumValues}
                value={generalSettings.maxStockToShow ?? ''}
                slotProps={{
                  inputLabel: {
                    shrink: generalSettings.maxStockToShow != null,
                    style: {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '90%',
                    },
                  },
                  htmlInput: { maxLength: 4, min: 0, step: 1 },
                }}
                onBlur={() => handleBlur()}
                onChange={(event) => {
                  const stringValue = onlyNumbers(event.target.value);
                  const numericValue = stringValue === '' ? null : parseInt(stringValue, 10);

                  handleChange({ name: 'maxStockToShow', value: numericValue });
                }}
              />
            </Tooltip>
          </Box>
        )}
      </Box>
    </SettingContainer>
  );
};

export default StockSectionGeneralSettings;
